<template>
  <span>
    <!--
      You can override the open slot to customize the button that opens this modal:
      <ExportProcessModal :corpus-id="corpusId">
        <button ... />
      </ExportProcessModal>
      Do not show this slot if the modal is being opened and closed using v-model.
    -->
    <slot
      name="open"
      :open="open"
    >
      <i
        class="icon-export"
        v-on:click.prevent="open"
      ></i>
    </slot>
  </span>
  <Modal
    v-if="opened"
    v-model="opened"
    :title="`Export ${exportTarget} as ${exportFormatLabel}`"
  >
    <div v-if="loading">
      <span class="loader is-size-2 mx-auto"></span>
    </div>
    <div
      v-else-if="!exportWorkerVersion"
      class="notification is-danger"
    >
      The worker version for {{ exportFormatLabel }} export could not be retrieved.
    </div>
    <template v-else>
      <!-- selection and element id errors -->
      <template v-if="formErrors.element_id">
        <p
          class="notification is-danger"
          v-for="err in formErrors.element_id"
          :key="err"
        >
          {{ err }}
        </p>
      </template>
      <template v-if="formErrors.selection">
        <p
          class="notification is-danger"
          v-for="err in formErrors.selection"
          :key="err"
        >
          {{ err }}
        </p>
      </template>

      <FormFields
        :worker-version="exportWorkerVersion"
        :corpus-id="corpusId"
      />

      <div
        class="columns m-0 mt-3"
        v-if="hasFeature('enterprise')"
      >
        <div class="column px-0 is-narrow">
          <label class="label mt-2">Farm</label>
        </div>
        <div class="column pr-0 pl-4">
          <div class="select is-fullwidth">
            <select v-model="farmId">
              <option :value="null">Use default farm</option>
              <template v-if="farms">
                <option
                  v-for="(farm, key) in farms"
                  :key="key"
                  :value="key"
                >
                  {{ farm.name }}
                </option>
              </template>
            </select>
          </div>
          <template v-if="formErrors.farm_id">
            <p
              class="help is-danger"
              v-for="err in formErrors.farm_id"
              :key="err"
            >
              {{ err }}
            </p>
          </template>
        </div>
      </div>
    </template>
    <template v-slot:footer="{ close }">
      <button
        class="button"
        v-on:click.prevent="close"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        :class="{ 'is-loading': loading }"
        v-on:click.prevent="startExport"
        :disabled="!canExport"
        :title="exportButtonTitle"
      >
        Export
      </button>
    </template>
  </Modal>
</template>

<script lang="ts">
import { isEmpty } from 'lodash'
import { mapActions, mapState, mapStores } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import type { CreateExportProcessParams } from '@/api/export'
import { EXPORT_FORMATS, UUID_REGEX } from '@/config'
import { errorParser, isArkindexError } from '@/helpers'
import { corporaMixin, truncateMixin } from '@/mixins'
import {
  useAuthStore,
  useElementStore,
  useNotificationStore,
  usePonosStore,
  useProcessStore,
  useWorkerConfigurationStore,
  useWorkerStore,
} from '@/stores'
import type { UUID } from '@/types'
import type { ExportFormat } from '@/types/export'
import type { WorkerVersion } from '@/types/worker'

import Modal from './Modal.vue'
import FormFields from './WorkerConfiguration/FormFields.vue'

export default defineComponent({
  components: {
    Modal,
    FormFields,
  },
  mixins: [corporaMixin, truncateMixin],
  props: {
    corpusId: {
      type: String as PropType<UUID>,
      validator: (value) => value === null || (typeof value === 'string' && UUID_REGEX.test(value)),
      required: true,
    },
    format: {
      type: String as PropType<ExportFormat>,
      required: true,
    },
    selection: {
      type: Boolean,
      default: false,
    },
    elementId: {
      type: String as PropType<UUID>,
      validator: (value) => value === null || (typeof value === 'string' && UUID_REGEX.test(value)),
      default: null,
    },
  },
  data: () => ({
    opened: false,
    loading: false,
    farmId: null as UUID | null,
    /**
     * Errors that this component has to display in the form:
     * everything but the configuration, which is handled by the configuration store and the FormFields component
     */
    formErrors: {} as { [k in keyof CreateExportProcessParams]?: string[] },
  }),
  computed: {
    ...mapState(useAuthStore, ['isVerified', 'hasFeature']),
    ...mapState(useElementStore, ['elements']),
    ...mapState(usePonosStore, ['farms']),
    ...mapStores(useWorkerStore, useWorkerConfigurationStore),
    hasAdminPrivilege(): boolean {
      return (this.isVerified && this.corpus && this.canAdmin(this.corpus)) ?? false
    },
    canExport(): boolean {
      return (
        !this.loading &&
        !this.workerConfigurationStore.isLoading &&
        this.workerConfigurationStore.isValid &&
        this.hasAdminPrivilege &&
        this.exportWorkerVersion !== null
      )
    },
    element() {
      if (this.elementId) return this.elements[this.elementId] || null
      return null
    },
    exportTarget(): string {
      if (this.selection)
        return `selected elements from project ${this.truncateShort(this.corpus?.name)}`
      if (this.element)
        return `${this.truncateShort(this.element.type)} ${this.truncateShort(this.element.name)}`
      return `project ${this.truncateShort(this.corpus?.name)}`
    },
    exportFormatLabel(): string {
      return EXPORT_FORMATS[this.format].label
    },
    exportWorkerVersion(): WorkerVersion | null {
      if (!this.format || !(this.format in EXPORT_FORMATS)) return null
      const id = this.workerStore.featureWorkerVersionIds[EXPORT_FORMATS[this.format].feature]
      return id ? this.workerStore.workerVersions[id] : null
    },
    exportButtonTitle() {
      if (this.loading) return 'Loading…'
      if (!this.exportWorkerVersion) return
      if (!this.workerConfigurationStore.isValid) return 'Please enter a valid configuration'
      return 'Start export'
    },
  },
  methods: {
    ...mapActions(useProcessStore, ['createExportProcess']),
    ...mapActions(useNotificationStore, ['notify']),
    ...mapActions(usePonosStore, ['listFarms']),
    open() {
      this.opened = true
    },
    async startExport() {
      const params: CreateExportProcessParams = {
        format: this.format,
      }
      if (!isEmpty(this.workerConfigurationStore.generatedConfiguration))
        params.configuration = this.workerConfigurationStore.generatedConfiguration
      if (this.hasFeature('enterprise')) params.farm_id = this.farmId
      if (this.elementId) params.element_id = this.elementId
      else if (this.selection) params.selection = true

      this.formErrors = {}
      try {
        const newProcess = await this.createExportProcess(this.corpusId, params)
        this.$router.push({
          name: this.hasFeature('enterprise') ? 'process-wait' : 'process-details',
          params: { id: newProcess.id },
        })
      } catch (err) {
        err = this.workerConfigurationStore.extractConfigurationErrors(err)
        if (isArkindexError(err)) {
          for (const error of err.response.data.errors) {
            if (
              error.attr !== null &&
              error.attr.length > 0 &&
              ['element_id', 'farm_id', 'format', 'selection'].includes(error.attr[0].toString())
            ) {
              const key = error.attr[0] as keyof CreateExportProcessParams
              if (this.formErrors[key]) this.formErrors[key].push(error.detail)
              else this.formErrors[key] = [error.detail]
            }
          }
        }
        this.notify({ type: 'error', text: errorParser(err) })
      }
    },
  },
  watch: {
    opened: {
      async handler(newValue) {
        if (newValue) {
          try {
            this.loading = true
            if (this.hasFeature('enterprise') && this.isVerified) {
              this.listFarms()
            }

            if (!this.format) return
            const feature = EXPORT_FORMATS[this.format].feature
            if (!(feature in this.workerStore.featureWorkerVersionIds)) {
              await this.workerStore.getFeatureWorkerVersion(feature)
            }

            this.workerConfigurationStore.editNew(
              this.workerStore.featureWorkerVersionIds[feature] as UUID,
            )
          } finally {
            this.loading = false
          }
        }
      },
      immediate: true,
    },
  },
})
</script>
